import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  AdminEmptyState,
  AdminErrorBanner,
  AdminPagination,
  AdminSearchBar,
} from './AdminTableChrome'
import {
  ADMIN_PAGE_SIZE,
  adminKeys,
  formatDateAr,
  type AdminOrganizationListResponse,
} from './types'

/**
 * Paginated, searchable cross-org organization table (read-only oversight).
 * Columns: organization (name over slug), owner (email), user count, quiz
 * count, created. Renders a semantic `<table>` from `md` up and collapses to
 * stacked label:value cards below it, matching the user/quiz tables.
 */
export function AdminOrgTable() {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [searchInput])

  const orgsQuery = useQuery({
    queryKey: adminKeys.organizations({ search, page }),
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      params.set('page', String(page))
      params.set('pageSize', String(ADMIN_PAGE_SIZE))
      const res = await fetch(`/api/v1/admin/organizations?${params.toString()}`, {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load organizations: ${res.status}`)
      }
      return (await res.json()) as AdminOrganizationListResponse
    },
  })

  const total = orgsQuery.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / ADMIN_PAGE_SIZE))
  const organizations = orgsQuery.data?.organizations ?? []

  return (
    <div>
      <div className="mb-6">
        <AdminSearchBar
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('searchOrganizations')}
        />
      </div>

      {orgsQuery.isLoading ? <TableSkeleton /> : null}

      {orgsQuery.isError ? <AdminErrorBanner message={t('loadError')} /> : null}

      {orgsQuery.isSuccess && organizations.length === 0 ? (
        <AdminEmptyState message={search ? t('noResults') : t('noOrganizations')} />
      ) : null}

      {orgsQuery.isSuccess && organizations.length > 0 ? (
        <>
          {/* Desktop: semantic table. */}
          <div className="hidden overflow-hidden rounded-2xl border border-brand-100 bg-white md:block">
            <table className="w-full border-collapse text-sm" dir="rtl">
              <thead>
                <tr className="border-b border-brand-100 bg-brand-50/40 text-xs font-semibold uppercase tracking-wide text-brand-600">
                  <th scope="col" className="px-4 py-3 text-start">{t('orgName')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('owner')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('usersCount')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('quizzesCount')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('userColCreated')}</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map((org) => (
                  <tr key={org.id} className="border-b border-brand-50 last:border-0 hover:bg-brand-50/30">
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-brand-900">{org.name}</div>
                      <div className="text-xs text-brand-600" dir="ltr">{org.slug}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700">
                      <span dir="ltr">{org.ownerEmail ?? '—'}</span>
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700 tabular-nums">
                      <span dir="ltr">{org.userCount}</span>
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700 tabular-nums">
                      <span dir="ltr">{org.quizCount}</span>
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700">
                      <span dir="ltr">{formatDateAr(org.createdAt)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked label:value cards. */}
          <ul role="list" className="space-y-4 md:hidden">
            {organizations.map((org) => (
              <li
                key={org.id}
                className="rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-500 ps-4 pe-4 py-4 shadow-sm"
              >
                <div className="font-semibold text-brand-900">{org.name}</div>
                <div className="text-xs text-brand-600" dir="ltr">{org.slug}</div>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-brand-500">{t('owner')}</dt>
                  <dd className="text-brand-800 text-end" dir="ltr">{org.ownerEmail ?? '—'}</dd>
                  <dt className="text-brand-500">{t('usersCount')}</dt>
                  <dd className="text-brand-800 text-end tabular-nums" dir="ltr">{org.userCount}</dd>
                  <dt className="text-brand-500">{t('quizzesCount')}</dt>
                  <dd className="text-brand-800 text-end tabular-nums" dir="ltr">{org.quizCount}</dd>
                  <dt className="text-brand-500">{t('userColCreated')}</dt>
                  <dd className="text-brand-800 text-end" dir="ltr">{formatDateAr(org.createdAt)}</dd>
                </dl>
              </li>
            ))}
          </ul>

          <AdminPagination
            page={page}
            pageCount={pageCount}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(pageCount, p + 1))}
          />
        </>
      ) : null}

      {orgsQuery.isLoading ? (
        <span className="sr-only" role="status" aria-live="polite">
          {tCommon('loading')}
        </span>
      ) : null}
    </div>
  )
}

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-brand-100 bg-white" aria-hidden="true">
      <div className="space-y-px">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-4 animate-pulse">
            <div className="h-6 flex-1 rounded bg-brand-50" />
            <div className="h-6 w-32 rounded bg-brand-50" />
            <div className="h-6 w-12 rounded bg-brand-50" />
            <div className="h-6 w-12 rounded bg-brand-50" />
            <div className="h-6 w-24 rounded bg-brand-50" />
          </div>
        ))}
      </div>
    </div>
  )
}
